import { useMemo } from "react";
import { CHART_HEIGHT } from "./constants";

export type Point = {
    x: number;
    y: number;
};

export type TemperaturePoints = {
    /** (x, y) coordinates of the minimum temperature points */
    minTempPoints: Point[];
    /** (x, y) coordinates of the maximum temperature points */
    maxTempPoints: Point[];
};

export type TemperatureCurves = {
    /** svg path string of Bezier curve associated with minimum temperature points */
    minTempsCurve: string;
    /** svg path string of Bezier curve associated with maximum temperature points */
    maxTempsCurve: string;
};

/**
 * Transform temperature data to data points on the svg graph.
 * @param minTemps Minimum monthly temperature data
 * @param maxTemps Maximum monthly temperature data
 * @param spacing The default space between the points
 * @param margin The default margin between the points
 */
const calculateTemperaturePoints = (
    minTemps: number[],
    maxTemps: number[],
    spacing: number,
    margin: number,
): TemperaturePoints => {
    const minTemp = Math.min(...minTemps);
    const maxTemp = Math.max(...maxTemps);

    const calculatePoint = (value: number, index: number): Point => {
        const bottom = 60;
        const height = 90;
        const relativeY =
            Math.floor(
                bottom + (height * (value - minTemp)) / (minTemp - maxTemp),
            ) || 0;
        return {
            x: spacing * index + margin,
            y: relativeY + CHART_HEIGHT / 2,
        };
    };

    const minTempPoints: Point[] = minTemps.map(calculatePoint);
    const maxTempPoints: Point[] = maxTemps.map(calculatePoint);

    return { minTempPoints, maxTempPoints };
};

/**
 * Calculate the vector between a starting and ending point.
 * @param pointA Starting point
 * @param pointB Ending point
 */
const calculateVector = (
    pointA: Point,
    pointB: Point,
): { length: number; angle: number } => {
    const lengthX = pointB.x - pointA.x;
    const lengthY = pointB.y - pointA.y;
    return {
        length: Math.sqrt(Math.pow(lengthX, 2) + Math.pow(lengthY, 2)),
        angle: Math.atan2(lengthY, lengthX),
    };
};

/**
 * Calculate the control point for a segment of the Bezier curve.
 * @param current Current point
 * @param previous Previous point
 * @param next Next point
 * @param reverse Whether to invert the vector angle (in order to calculate the end point)
 */
const calculateControlPoint = (
    current: Point,
    previous: Point,
    next: Point,
    reverse?: boolean,
): Point => {
    const smoothing = 0.2;
    const point = previous;
    const nextPoint = next;
    const line = calculateVector(point, nextPoint);
    const angle = line.angle + (reverse ? Math.PI : 0);
    const length = line.length * smoothing;
    const x = current.x + Math.cos(angle) * length;
    const y = current.y + Math.sin(angle) * length;
    return { x, y };
};

/**
 * Builds one segment of the Bezier curve at a given point.
 * @param point
 * @param index
 * @param points
 */
const calculateBezierCurve = (
    point: Point,
    index: number,
    points: Point[],
): string => {
    const cps = calculateControlPoint(
        points[index - 1] || point,
        points[index - 2] || points[index - 1] || point,
        point,
    );
    const cpe = calculateControlPoint(
        point,
        points[index - 1] || point,
        points[index + 1] || point,
        true,
    );
    return `C ${cps.x},${cps.y} ${cpe.x},${cpe.y} ${point.x},${point.y}`;
};

/**
 * Builds a svg path from the temperature data points.
 * @param temperaturePoints Temperature data points
 */
const calculateTemperatureCurve = (temperaturePoints: Point[]): string => {
    return temperaturePoints.reduce(
        (graph: string, point: Point, index, points) => {
            if (index === 0) {
                graph = `M${point.x} ${point.y}`;
            } else {
                graph += calculateBezierCurve(point, index, points);
            }
            return graph;
        },
        "",
    );
};

export const useTemperatureData = (
    minTemps: number[],
    maxTemps: number[],
    spacing: number,
    margin: number,
): TemperaturePoints & TemperatureCurves => {
    return useMemo(() => {
        const { minTempPoints, maxTempPoints } = calculateTemperaturePoints(
            minTemps,
            maxTemps,
            spacing,
            margin,
        );

        return {
            minTempPoints,
            maxTempPoints,
            minTempsCurve: calculateTemperatureCurve(minTempPoints),
            maxTempsCurve: calculateTemperatureCurve(maxTempPoints),
        };
    }, [minTemps, maxTemps, spacing, margin]);
};
